import { motion } from 'framer-motion';
import OptimizedImage from './OptimizedImage';

/**
 * Abstract product-system visual for the hero — layered interface fragments
 * inspired by premium SaaS dashboards, using brand blues rather than reference purples.
 */
export default function HeroVisual() {
  return (
    <div className="relative w-full max-w-lg mx-auto lg:max-w-none" aria-hidden="true">
      {/* Ambient glow */}
      <div className="absolute -inset-8 bg-radial-accent opacity-60 blur-3xl pointer-events-none" />

      {/* Blueprint grid frame */}
      <div className="absolute inset-0 blueprint-grid rounded-2xl pointer-events-none" />

      {/* Layered cards */}
      <div className="relative aspect-square max-h-[480px]">
        {/* Back card — code block */}
        <motion.div
          initial={{ opacity: 0, y: 24, rotate: -2 }}
          animate={{ opacity: 1, y: 0, rotate: -2 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="absolute top-4 right-0 w-[72%] glass-card p-4 shadow-elevated"
        >
          <div className="flex items-center gap-1.5 mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
            <span className="ml-2 text-[10px] font-mono text-gray-500 dark:text-gray-400">ProductEngine.tsx</span>
          </div>
          <pre className="text-[11px] leading-relaxed font-mono text-gray-600 dark:text-gray-300 overflow-hidden">
            <code>{`interface ProductEngineer {
  focus: 'UX' | 'React' | 'TypeScript';
  ship: (idea: Idea) => Software;
}`}</code>
          </pre>
        </motion.div>

        {/* Middle card — profile */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="absolute top-[18%] left-[8%] w-[58%] aspect-square rounded-2xl overflow-hidden border border-white/20 dark:border-white/10 shadow-elevated"
        >
          <OptimizedImage
            src="/profile.jpg"
            alt="Portrait of Julian Querido, Product Engineer"
            className="w-full h-full object-cover"
            sizes="(min-width: 1024px) 40vw, 60vw"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-linear-to-t from-gray-900/40 via-transparent to-transparent" />
        </motion.div>

        {/* Front card — UI metrics */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="absolute bottom-6 left-0 w-[78%] glass-card p-4 shadow-elevated"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Delivery focus</span>
            <span className="text-[10px] font-mono text-blue-600 dark:text-blue-400">shipped</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'UX', value: 'First' },
              { label: 'Stack', value: 'React' },
              { label: 'Ship', value: 'Clean' },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-lg bg-gray-50/80 dark:bg-gray-800/60 border border-gray-200/60 dark:border-gray-700/60 px-2 py-2 text-center"
              >
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">{item.label}</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.value}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Accent orb */}
        <div className="absolute -bottom-2 -right-2 w-20 h-20 rounded-full bg-blue-500/20 dark:bg-blue-400/15 blur-2xl pointer-events-none" />
      </div>
    </div>
  );
}
